import { useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import Button from "components/Button";
import ModalComponent from "components/ModalComponent";
import PatientQuoteCreateForm from "pages/Quotes/PatientQuote/components/Forms/PatientQuoteCreateForm";
import ProfessionalQuoteCreateForm from "pages/Quotes/ProfessionalQuote/components/Forms/ProfessionalQuoteCreateForm";
import QuoteCreateForm from "pages/Quotes/ProfessionalQuote/components/Forms/QuoteCreateForm";
import SecretaryQuoteCreateForm from "pages/Quotes/ProfessionalQuote/components/Forms/SecretaryQuoteCreateForm";
import { useAuth } from "providers/Auth";
import { useProfessionalQuote } from "providers/Quote/ProfessionalQuote";
import { PaymentFeeOwner, PaymentTool, RoleDescription } from "types/enums";
import { SimulatorParcel } from "types/quotes";
import { formatAsPercent, formatPrice } from "utils/formats";

import * as S from "../../styles";

interface SimulatorParcelCardProps {
  simulatorParcel: SimulatorParcel;
  company: { value: number; label: string };
  showAddQuote: boolean;
  paymentToolAndMachine?: {
    paymentTool: PaymentTool | null;
    cardMachine: { value: number; label: string };
  };
}

const SimulatorParcelCard = ({
  simulatorParcel,
  company,
  showAddQuote,
  paymentToolAndMachine,
}: SimulatorParcelCardProps) => {
  const { userRole } = useAuth();
  const [addQuoteModalShow, setAddQuoteModalShow] = useState(false);
  const { simulatedQuotePayment, setSimulatedQuotePayment } = useProfessionalQuote();

  const createQuote = () => {
    setSimulatedQuotePayment({ ...simulatedQuotePayment, ...simulatorParcel });
    setAddQuoteModalShow(true);
  };

  const showFee = simulatedQuotePayment.paymentFeeOwner === PaymentFeeOwner.Paciente;
  const showCompensation = simulatedQuotePayment.paymentFeeOwner === PaymentFeeOwner.Estabelecimento;

  return (
    <>
      <S.ParcelCard>
        <S.ParcelGrid>
          <S.ParcelMeta>
            <S.ParcelMetaLabel>Parcelas</S.ParcelMetaLabel>
            <S.ParcelMetaValue className="primary">
              {simulatorParcel.parcelQtd}×
            </S.ParcelMetaValue>
          </S.ParcelMeta>

          <S.ParcelMeta>
            <S.ParcelMetaLabel>Valor da parcela</S.ParcelMetaLabel>
            <S.ParcelMetaValue>{formatPrice(simulatorParcel.parcelValue)}</S.ParcelMetaValue>
          </S.ParcelMeta>

          {showFee && simulatorParcel.totalValue > 0 && (
            <S.ParcelMeta>
              <S.ParcelMetaLabel>
                CET
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      <strong>Custo Efetivo Total (CET):</strong> percentual que
                      resume todos os encargos da operação (juros, taxas e
                      demais custos) em relação ao valor da transação.
                    </Tooltip>
                  }
                >
                  <i className="uil uil-info-circle" style={{ cursor: "help" }} />
                </OverlayTrigger>
              </S.ParcelMetaLabel>
              <S.ParcelMetaValue className="muted">
                {simulatorParcel.cetCost != null
                  ? formatAsPercent(simulatorParcel.cetCost)
                  : "—"}
              </S.ParcelMetaValue>
            </S.ParcelMeta>
          )}

          <S.ParcelMeta>
            <S.ParcelMetaLabel>Total</S.ParcelMetaLabel>
            <S.ParcelMetaValue>{formatPrice(simulatorParcel.totalValue)}</S.ParcelMetaValue>
          </S.ParcelMeta>

          {showCompensation && (
            <S.ParcelMeta>
              <S.ParcelMetaLabel>Compensação</S.ParcelMetaLabel>
              <S.CompensationPill>
                {formatPrice(simulatorParcel.compensationValue)}
              </S.CompensationPill>
            </S.ParcelMeta>
          )}
        </S.ParcelGrid>

        {!!showAddQuote && (
          <S.ParcelAction>
            <Button type="button" className="btn-primary btn-sm" onClick={createQuote}>
              <i className="uil uil-plus me-1" />
              Criar orçamento
            </Button>
          </S.ParcelAction>
        )}
      </S.ParcelCard>

      {!!showAddQuote && (
        <ModalComponent
          title="Novo Orçamento"
          show={addQuoteModalShow}
          onHide={() => setAddQuoteModalShow(false)}
        >
          {userRole === RoleDescription.Professional && (
            <ProfessionalQuoteCreateForm
              companyFromSimulator={company}
              paymentToolAndMachineFromSimulator={paymentToolAndMachine}
              onHide={() => setAddQuoteModalShow(false)}
            />
          )}
          {userRole === RoleDescription.Admin && (
            <QuoteCreateForm
              companyFromSimulator={company}
              paymentToolAndMachineFromSimulator={paymentToolAndMachine}
              onHide={() => setAddQuoteModalShow(false)}
            />
          )}
          {userRole === RoleDescription.Secretary && (
            <SecretaryQuoteCreateForm
              companyFromSimulator={company}
              paymentToolAndMachineFromSimulator={paymentToolAndMachine}
              onHide={() => setAddQuoteModalShow(false)}
            />
          )}
          {userRole === RoleDescription.Patient && (
            <PatientQuoteCreateForm
              companyFromSimulator={company}
              onHide={() => setAddQuoteModalShow(false)}
            />
          )}
        </ModalComponent>
      )}
    </>
  );
};

export default SimulatorParcelCard;
